/**
 * Camera settings tabs — single source of truth for tab id, label and order.
 *
 * Static order matches Figma frame 614:59371 (Drafts-1); agent tabs follow
 * the VMS-188 "Agents settings" frames: every per-camera agent gets its own
 * tab between Motion detection and Privacy blur — in single-camera AND batch
 * (multi-camera) mode.
 *
 * Agent tabs are dynamic (they come from the live agents list), so the full
 * tab list is built per render via buildSettingsTabs / buildBatchSettingsTabs
 * — see lib/agentMenus.ts for how manifests fold into menus. An agent tab id
 * is `agent:<menuId>`; `hasDivider: true` draws the sidebar separator above the
 * tab (Motion detection opens the agent group, Privacy blur closes it).
 *
 * `capabilityKey` marks the tabs that carry an agent-capability icon in the
 * rail — the same glyphs the cameras list shows per camera. A menu's id IS
 * its capability group, so agent tabs resolve straight through
 * getCapabilityForGroup; Motion detection is the one capability with a static
 * (non-agent) tab, so it carries its key literally.
 */

import { getCapabilityForGroup } from '@/lib/agentIcons';
import type { AgentMenu, SettingsTab } from '@/types/batch';
import type { NonEmpty } from '@/types/common';

export const AGENT_TAB_PREFIX = 'agent:';
export const WEB_PAGE_TAB_ID = 'webpage';

export const SETTINGS_TABS: NonEmpty<SettingsTab> = [
    { id: 'general', label: 'General' },
    { id: 'video', label: 'Video' },
    { id: 'audio', label: 'Audio' },
    { id: 'recording', label: 'Recording' },
    { id: 'motion', label: 'Motion detection', hasDivider: true, capabilityKey: 'motion' },
    { id: 'privacy', label: 'Privacy blur', hasDivider: true },
    { id: WEB_PAGE_TAB_ID, label: 'Web page' },
];

const getAgentTabs = (agentMenus?: readonly AgentMenu[] | null): SettingsTab[] =>
    (agentMenus || []).map((menu) => ({
        id: menu.tabId,
        label: menu.label,
        capabilityKey: getCapabilityForGroup(menu.id)?.key,
    }));

/**
 * Full single-camera tab list: agent menu tabs slot in right after Motion.
 * isMotionAvailable false drops the Motion tab, holding it to the same rule as
 * every other agent — no manifest on the camera's server, no tab. The Web page
 * tab is in only when isWebPageAvailable says the camera has one to show.
 */
export function buildSettingsTabs(
    agentMenus?: readonly AgentMenu[] | null, isMotionAvailable = true, isWebPageAvailable = false,
): SettingsTab[] {
    const motionIndex = SETTINGS_TABS.findIndex((tab) => tab.id === 'motion');
    const staticHead = SETTINGS_TABS.slice(0, motionIndex + 1)
        .filter((tab) => isMotionAvailable || tab.id !== 'motion');
    const staticTail = SETTINGS_TABS.slice(motionIndex + 1)
        .filter((tab) => isWebPageAvailable || tab.id !== WEB_PAGE_TAB_ID);
    return [
        ...staticHead,
        ...getAgentTabs(agentMenus),
        ...staticTail,
    ];
}

/**
 * The Web page tab exists for one camera that has a web page, and only for an
 * administrator: the proxied session behind it is minted for administrators
 * alone, so anyone else would only reach a refusal.
 */
export function isWebPageAvailable(
    cameras: readonly { webUrl?: string }[] | null, canManage: boolean,
): boolean {
    return canManage && cameras !== null && cameras.length === 1 &&
        Boolean(cameras[0]?.webUrl);
}

export function isAgentTab(id?: string): boolean {
    return typeof id === 'string' && id.startsWith(AGENT_TAB_PREFIX);
}

// Static tabs available when several cameras are selected (batch mode).
// General is included but the batch section only offers credentials — names
// are per-camera. Motion offers everything non-spatial — the excluding masks
// are painted per camera.
export const BATCH_TABS: SettingsTab[] = SETTINGS_TABS.filter(
    (tab) => ['general', 'video', 'recording', 'motion', 'privacy'].includes(tab.id));

/** Full batch tab list: same per-agent tabs as single mode, after Motion. */
export function buildBatchSettingsTabs(
    agentMenus?: readonly AgentMenu[] | null, isMotionAvailable = true,
): SettingsTab[] {
    const staticTabs = BATCH_TABS.filter((tab) => isMotionAvailable || tab.id !== 'motion');
    return [...staticTabs, ...getAgentTabs(agentMenus)];
}

export const DEFAULT_TAB_ID = 'general';

export function findTab(id?: string): SettingsTab {
    return SETTINGS_TABS.find((tab) => tab.id === id) ?? SETTINGS_TABS[0];
}

export function isValidTab(id?: string): boolean {
    return isAgentTab(id) || SETTINGS_TABS.some((tab) => tab.id === id);
}

export function isValidBatchTab(id?: string): boolean {
    return isAgentTab(id) || BATCH_TABS.some((tab) => tab.id === id);
}
